import { useQuery } from '@tanstack/react-query';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';

import Api from '@barboxx/api';
import { PourFormat } from '@barboxx/models/types';

interface Props {
    disabled?: boolean;
}

interface PourFormatWatchProps {
    pourFormat: PourFormat;
}

export function PourFormatInput({ disabled = false }: Props) {
    const { pourFormat } = useFormContext<PourFormatWatchProps>().getValues();
    const pourFormatQueryResult = useQuery({
        queryKey: ['pourFormats'],
        queryFn: () =>
            Api.Servings.PourFormat.getPourFormats({
                page: 0,
                size: 1000,
                sort: 'id,asc',
            }),
        initialData: {
            ok: true,
            problem: null,
            originalError: null,
            data: {
                results: pourFormat
                    ? [pourFormat as PourFormat.DetailsResponse]
                    : [],
                total: 1,
                isPaginated: false,
            },
        },
    });

    return (
        <AutocompleteElement
            label="Pour Format"
            name="pourFormatId"
            loading={pourFormatQueryResult.isLoading}
            multiple={false}
            options={pourFormatQueryResult.data?.data?.results ?? []}
            transform={{
                output(event, value, reason, details) {
                    if (reason === 'selectOption') {
                        return typeof value === 'string' ? value : value?.id;
                    }
                    if (reason === 'removeOption') {
                        return null;
                    }
                    return value;
                },
            }}
            autocompleteProps={{
                fullWidth: true,
                readOnly: disabled,
                disabled: disabled,
                isOptionEqualToValue(
                    option?: PourFormat.DetailsResponse,
                    value?: PourFormat | number | null
                ) {
                    const isNumber = typeof value === 'number';

                    if (isNumber) {
                        return option?.id === value;
                    } else {
                        return option?.id === (value?.id ?? value);
                    }
                },
                getOptionLabel(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return pourFormatName(option);
                },
            }}
        />
    );
}

export function pourFormatName(pourFormat?: PourFormat.DetailsResponse) {
    if (!pourFormat) {
        return '';
    }

    const {
        productCategory,
        servingContainer,
        packagingFormat,
        pourType,
        volumeOz,
    } = pourFormat;

    const containerName = servingContainer?.name || packagingFormat?.name;

    return `${pourType.name} - ${containerName} - ${volumeOz}oz${productCategory ? ` (${productCategory.name})` : ''}`;
}
